/**
 * useDateMaps - Hook for Date Record Maps
 *
 * Extracts date map computation logic from WorkflowStatusSidebar.
 * Generates date records maps by tax year and payment date for table display.
 *
 * Usage:
 * ```tsx
 * const dateMaps = useDateMaps(dateRecords, dateInputs, sidebarMode === "expanded" && selectedTab === "dates");
 * ```
 */

import { useMemo } from 'react';

import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import type {
  DateInputsForm,
  DateRecord,
  DateRecordsMapByPaymentDate,
  DateRecordsMapByTaxYear,
  ProjectDateRecordByYear,
} from '~/types';

export interface DateMapsResult {
  byTaxYear: DateRecordsMapByTaxYear | null;
  byPaymentDate: DateRecordsMapByPaymentDate | null;
  error: string | null;
}

/**
 * Hook to generate date record maps for table display
 *
 * @param dateRecords - Array of date records from store
 * @param dateInputs - Date inputs form from store
 * @param enabled - Whether to compute maps (e.g., when sidebar is expanded and dates tab selected)
 * @returns Object with byTaxYear and byPaymentDate maps, plus error state
 */
export const useDateMaps = (
  dateRecords: DateRecord[] | null,
  dateInputs: DateInputsForm | null,
  enabled: boolean
): DateMapsResult => {
  return useMemo(() => {
    if (!enabled || !dateRecords || !dateInputs) {
      return { byTaxYear: null, byPaymentDate: null, error: null };
    }

    try {
      const projectDateRecords: ProjectDateRecordByYear[] = dateRecords.map(
        (record: DateRecord, index: number) => ({
          ...record,
          projectYear: index + 1,
        })
      );

      const dateInputsEngineFormat = DateBuilder.convertToEngineFormat(dateInputs);
      const dateRecordsMapByTaxYear = DateBuilder.buildDateRecordsMapByTaxYear(projectDateRecords);
      const dateRecordsMapByPaymentDate = DateBuilder.buildDateRecordsMapByPaymentDate(
        projectDateRecords,
        dateInputsEngineFormat
      );

      return {
        byTaxYear: dateRecordsMapByTaxYear,
        byPaymentDate: dateRecordsMapByPaymentDate,
        error: null,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate date maps';
      console.error('Error generating date record maps:', error);
      return {
        byTaxYear: null,
        byPaymentDate: null,
        error: errorMessage,
      };
    }
  }, [dateRecords, dateInputs, enabled]);
};
